import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
} from "@mui/material";
import { useUsuario } from "../../../providers/usuarioProvider";
import { Moment } from "moment";
import {
  IconFileInvoice,
  IconWorld,
  IconCash,
  IconFileUpload,
  IconDownload,
} from "@tabler/icons-react";
import { LoadingButton } from "@mui/lab";
import { saveAs } from "file-saver";
import Functions from "../../../callable/functions";
import { useSnackBar } from "../../../stores/snackBarStores";
import RenderAnimation from "../../util/ui/renderAnimation";
import MoedaText from "../../util/ui/inputs/moedaText";
import FelixCard from "../../util/ui/felixCard";
import TabelaCustodiaInternacional from "../../util/tabelas/tabelaCustodiaInternacional";
import ImpostoInternacional from "../../../models/impostoInternacional";
import ResultadoInternacional from "../../../models/resultadoInternacional";
import { onSnapshot, query, orderBy } from "firebase/firestore";
import PosicaoInternacionalDb from "../../../db/posicaoInternacionalDb";
import { totalCustodiaBRL, totaisCustodiaPorMoeda } from "./totaisCustodia";
import PosicaoAtivoInternacional from "../../../models/posicaoAtivoInternacional";
import Atualizar from "../home/atualizar";
import RendimentosInternacionais from "./cards/rendimentosInternacionais";
import CustodiaInternacionalCompacta from "./cards/custodiaInternacionalCompacta";
import LancamentosDoMes from "./cards/lancamentosDoMes";
import { Link } from "react-router-dom";

type Props = {
  mes: Moment;
  imposto?: ImpostoInternacional;
  resultado?: ResultadoInternacional;
};

export default function MesInternacional({ mes, imposto, resultado }: Props) {
  const { visualizado } = useUsuario();
  const dispatchSnack = useSnackBar();
  const [posicoes, setPosicoes] = useState<PosicaoAtivoInternacional[]>([]);
  const [loadingPosicoes, setLoadingPosicoes] = useState(true);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);

  // PDF "Apuração Internacional" do mês, gerado no backend (mesma infra do
  // Controle Fiscal nacional). Só faz sentido com o mês calculado.
  const baixarRelatorio = async () => {
    if (!imposto) return;
    setGerandoRelatorio(true);
    try {
      const { data: url } = await new Functions().relatorioInternacional({
        data: imposto.dataBase.format(),
      });
      saveAs(url);
    } catch (error) {
      console.error(error);
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem:
            "Não foi possível gerar o relatório. Tente novamente em instantes.",
          severidade: "error",
        },
      });
    } finally {
      setGerandoRelatorio(false);
    }
  };

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        query(
          new PosicaoInternacionalDb().collection({
            usuarioId: visualizado,
            posicaoId: "",
          }),
          orderBy("data", "desc")
        ),
        (snapshot) => {
          setLoadingPosicoes(true);
          if (!snapshot.empty) {
            // Busca todos os documentos do mês e pega o mais recente (primeiro da lista ordenada desc)
            const documentosDoMes = snapshot.docs.filter(
              (doc) =>
                doc.data().data.format("YYYY-MM") === mes.format("YYYY-MM")
            );

            if (documentosDoMes.length > 0) {
              // Como está ordenado por data desc, o primeiro é o mais recente do mês
              const documentoMaisRecente = documentosDoMes[0];
              const posicoesPositivas = documentoMaisRecente
                .data()
                .posicoes.filter((posicao) => posicao.quantidade > 0);
              setPosicoes(posicoesPositivas);
            } else {
              setPosicoes([]);
            }
          } else {
            setPosicoes([]);
          }
          setLoadingPosicoes(false);
        }
      );
    }
  }, [visualizado, mes]);

  // Opção lançada (posição vendida) é obrigação, não bem: fica fora do total.
  const valorTotalCustodia = useMemo(
    () => totalCustodiaBRL(posicoes),
    [posicoes]
  );
  const totaisPorMoeda = useMemo(
    () => totaisCustodiaPorMoeda(posicoes),
    [posicoes]
  );

  const anoRecolhimento = mes.year() + 1;

  const CardImposto = () => (
    <RenderAnimation
      key="Imposto"
      delay={1 * 150}
      render={() => (
        <FelixCard sx={{ height: "100%" }}>
          <CardHeader
            title="Imposto Internacional"
            avatar={<IconFileInvoice size={16} />}
          />
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center">
              {/* Número grande em mono tabular — assinatura #1 do sistema. */}
              <Typography
                color="text.primary"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  fontSize: 28,
                  letterSpacing: "-0.02em",
                  paddingBottom: 2,
                }}
              >
                {imposto ? (
                  <MoedaText value={imposto.impostoAnualDevido} />
                ) : (
                  <Skeleton variant="text" width={100} animation={false} />
                )}
              </Typography>
            </Box>
            <Box
              display="flex"
              alignItems="flex-end"
              justifyContent="center"
              gap={2}
            >
              <Box
                display="flex"
                flexShrink={1}
                flexDirection="column"
                alignItems="center"
                px={2}
              >
                <Typography
                  variant="dataMono"
                  color="text.primary"
                  sx={{ fontWeight: 500 }}
                >
                  {imposto ? (
                    <MoedaText value={imposto.impostoMensal} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Apurado no mês
                </Typography>
              </Box>
              <Divider
                sx={{ m: 1 }}
                orientation="vertical"
                variant="middle"
                flexItem
              />
              <Box
                display="flex"
                flexShrink={1}
                flexDirection="column"
                alignItems="center"
                px={2}
              >
                <Typography
                  variant="dataMono"
                  color="text.primary"
                  sx={{ fontWeight: 500 }}
                >
                  {imposto ? (
                    <MoedaText value={imposto.impostoAnualDevido} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Acumulado a recolher em {anoRecolhimento}
                </Typography>
              </Box>
            </Box>
            {/* Imposto pago no exterior (Lei 14.754/2023): retido na fonte no
                ano e o que sobra para pagar na DIRPF depois da dedução por
                bem. O anual devido acima segue sendo os 15% cheios. */}
            <Divider sx={{ my: 1.5 }} />
            <Box
              display="flex"
              alignItems="flex-end"
              justifyContent="center"
              gap={2}
            >
              <Box
                display="flex"
                flexShrink={1}
                flexDirection="column"
                alignItems="center"
                px={2}
              >
                <Typography
                  variant="dataMono"
                  color="text.primary"
                  sx={{ fontWeight: 500 }}
                >
                  {imposto ? (
                    <MoedaText value={imposto.impostoRetidoAno} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Retido no exterior no ano
                </Typography>
              </Box>
              <Divider
                sx={{ m: 1 }}
                orientation="vertical"
                variant="middle"
                flexItem
              />
              <Box
                display="flex"
                flexShrink={1}
                flexDirection="column"
                alignItems="center"
                px={2}
              >
                <Typography
                  variant="dataMono"
                  sx={(theme) => ({
                    fontWeight: 500,
                    color: theme.palette.statusChip.warn.fg,
                  })}
                >
                  {imposto ? (
                    <MoedaText value={imposto.impostoEstimadoDIRPF} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Estimativa a pagar na DIRPF {anoRecolhimento}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </FelixCard>
      )}
    />
  );

  // `.dark` escurece sobre fundo escuro e perde contraste — o token semântico
  // do sistema já troca por tema (DESIGN.md → Accents & Semantic).
  const getColor = (numero: number) => {
    if (numero < 0) return "error.main";
    if (numero === 0) return "text.primary";
    if (numero > 0) return "success.main";
  };

  const CardResultado = () => (
    <RenderAnimation
      key="Resultado"
      delay={2 * 150}
      render={() => (
        <FelixCard sx={{ height: "100%" }}>
          <CardHeader
            title="Resultado Internacional"
            avatar={<IconCash size={16} />}
          />
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography
                color={getColor(resultado?.resultadoTotal ?? 0)}
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  fontSize: 28,
                  letterSpacing: "-0.02em",
                  paddingBottom: 2,
                }}
              >
                {resultado ? (
                  <MoedaText value={resultado.resultadoTotal} />
                ) : (
                  <Skeleton variant="text" width={100} animation={false} />
                )}
              </Typography>
            </Box>
            <Box display="flex" alignItems="flex-end">
              <Box
                display="flex"
                flexShrink={1}
                width="100%"
                flexDirection="column"
                alignItems="center"
              >
                <Typography
                  variant="dataMono"
                  color={getColor(resultado?.ganhoCapital ?? 0)}
                  sx={{ fontWeight: 500 }}
                >
                  {resultado ? (
                    <MoedaText value={resultado.ganhoCapital} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Ganho Capital
                </Typography>
              </Box>
              <Divider
                sx={{ m: 1 }}
                orientation="vertical"
                variant="middle"
                flexItem
              />
              <Box
                display="flex"
                flexShrink={1}
                width="100%"
                flexDirection="column"
                alignItems="center"
              >
                <Typography
                  variant="dataMono"
                  color={getColor(resultado?.rendimentos ?? 0)}
                  sx={{ fontWeight: 500 }}
                >
                  {resultado ? (
                    <MoedaText value={resultado.rendimentos} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Rendimentos
                </Typography>
              </Box>
              <Divider
                sx={{ m: 1 }}
                orientation="vertical"
                variant="middle"
                flexItem
              />
              <Box
                display="flex"
                flexShrink={1}
                width="100%"
                flexDirection="column"
                alignItems="center"
              >
                <Typography
                  variant="dataMono"
                  color={getColor(resultado?.resultadoAcumulado ?? 0)}
                  sx={{ fontWeight: 500 }}
                >
                  {resultado ? (
                    <MoedaText value={resultado.resultadoAcumulado} />
                  ) : (
                    <Skeleton variant="text" width={60} animation={false} />
                  )}
                </Typography>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 0.5 }}
                >
                  Acumulado
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </FelixCard>
      )}
    />
  );

  const CardCustodia = () => (
    <RenderAnimation
      key="Custodia"
      delay={3 * 150}
      render={() => (
        <FelixCard sx={{ height: "100%" }}>
          <CardHeader
            title="Custódia Internacional"
            avatar={<IconWorld size={16} />}
          />
          <CardContent>
            {posicoes.length > 0 ? (
              <TabelaCustodiaInternacional
                posicoes={posicoes}
                loading={false}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={4}
              >
                Nenhuma posição encontrada para este mês.
              </Typography>
            )}
          </CardContent>
        </FelixCard>
      )}
    />
  );

  return (
    <Container
      maxWidth="lg"
      sx={{
        padding: {
          md: 1,
          xs: 0,
        },
      }}
    >
      <Grid container alignItems="stretch">
        <Grid item sx={{ p: 1 }} style={{ alignSelf: "center" }} xs="auto">
          <RenderAnimation
            key="Mês"
            delay={0 * 150}
            render={() => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap={1}
              >
                <Box>
                  <Typography variant="eyebrow" color="text.secondary">
                    Competência
                  </Typography>
                  {/* Mês é data: mono tabular, uppercase tracked. */}
                  <Typography
                    color="text.primary"
                    sx={(theme) => ({
                      fontFamily: theme.typography.dataMono.fontFamily,
                      fontVariantNumeric: "tabular-nums",
                      display: "block",
                      fontSize: 20,
                      fontWeight: 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    })}
                  >
                    {mes.format("MMMM/YY")}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<IconFileUpload />}
                  fullWidth
                  component={Link}
                  to="/internacional/arquivo"
                >
                  Gerenciar Statements
                </Button>
                <LoadingButton
                  variant="outlined"
                  color="secondary"
                  startIcon={<IconDownload />}
                  fullWidth
                  disabled={!imposto}
                  loading={gerandoRelatorio}
                  loadingPosition="start"
                  onClick={baixarRelatorio}
                >
                  Baixar Relatório
                </LoadingButton>
              </Box>
            )}
          />
        </Grid>
        <Grid item xs={true} sx={{ p: 1 }}>
          <RenderAnimation
            key="Status"
            delay={1 * 150}
            render={() => <Atualizar />}
          />
        </Grid>
        <Grid item xs="auto" sx={{ p: 1 }}>
          <RenderAnimation
            key="CustodiaCompacta"
            delay={0 * 300}
            render={() => (
              <CustodiaInternacionalCompacta
                valorTotalCustodia={valorTotalCustodia}
                totaisPorMoeda={totaisPorMoeda}
                loading={loadingPosicoes}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}></Grid>

        {/* Row 1: Resultado e Imposto */}
        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <CardResultado />
        </Grid>
        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <CardImposto />
        </Grid>

        {/* Row 2: Custódia */}
        <Grid item xs={12} sx={{ p: 1 }}>
          <CardCustodia />
        </Grid>

        {/* Row 3: Lançamentos importados no mês */}
        <Grid item xs={12} sx={{ p: 1 }}>
          <RenderAnimation
            key="Lancamentos"
            delay={4 * 150}
            render={() => <LancamentosDoMes mes={mes} />}
          />
        </Grid>

        {/* Row 4: Rendimentos */}
        <Grid item xs={12} sx={{ p: 1 }}>
          <RenderAnimation
            key="Rendimentos"
            delay={5 * 150}
            render={() => (
              <RendimentosInternacionais
                detalhesPorMoeda={resultado?.detalhesPorMoeda}
                rendimentosTotal={resultado?.rendimentos}
              />
            )}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
